<template>
    <div>
        <div class="flex items-center at1024:flex-row flex-col justify-center at1024:justify-between border-b dark:border-gray-850">
            <Breadcrumbs
                class="block !border-0"
                :crumbs="crumbs"
                h2style="text-base at1024:text-xl"
            />
            <div class="resource-select w-full at1024:w-80 at1024:mr-8">
                <SeclockMultiselect
                    :option-is-object="true"
                    label-by="display_name"
                    track-by="display_name"
                    value-by="display_name"
                    v-model="selectedManufacturer"
                    :options="manufacturers"
                    :searchable="true"
                    :placeholder="$t('Manufacturers')"
                    :can-clear="false"
                    mode="single"
                    @select="navigateToShow($event)"
                />
            </div>
        </div>
        <LoadingProgress :is-loading="inert">
        <div class="relative flex flex-col gap-8 w-full justify-between px-4 at1024:px-8 py-16 at1024:flex-row at1024:gap-16">
            <h1 class="sr-only">{{ manufacturer.name }}</h1>
            <header
                class="bg-gray-125 flex h-fit justify-center shrink-0 w-full at1024:sticky at1024:bg-white at1024:w-1/5"
                :class="settings.is_sourcing_mode? 'at1024:top-36': 'at1024:top-48'"
            >
                <ManufacturerDetails
                    :name="manufacturer.name"
                    :logo="marketingAssetUrl(manufacturer.logo)"
                    :logo-alt-text="manufacturer.name + ' logo'"
                    :phone="manufacturer.channels.find((channel) => channel?.pivot.type === 1)?.channel"
                    :website="manufacturer.channels.find((channel) => channel?.pivot.type === 4)?.channel"
                    :small="true"
                    :tags=" manufacturer.tags['item-category']?.map((tag) => tag.name)"
                />
            </header>
            <article class="grow">
                <h3 class="font-bold mb-1 text-gray-400 text-2xl at1024:text-3xl">{{ manufacturer.name }}</h3>
                <div class="flex items-center mb-4">
                    <SeclockMultiselect
                        v-model="selectedSeries"
                        :options="computed_series ?? []"
                        placeholder="All Series"
                        :close-on-select="true"
                        :multiple="true"
                        :searchable="true"
                        :hide-selected="true"
                        label-by="name"
                        track-by="name"
                        value-by="name"
                        :option-is-object="true"
                        :large="true"
                        :disabled="!computed_series.length"
                        class="bg-transparent grow single-manufacturer-select"
                    />
                </div>
                <ResourceList v-if="filteredResources" :resources="filteredResources"/>
                <RecordsNotFound v-else icon="icon-draft-paper" header="Resources unavailable"/>
            </article>
            <nav class="h-fit w-full shrink-0 at1024:w-1/5">
                <div class="font-medium mb-4 text-gray-800 dark:text-gray-350">{{ $t("Browse products") }}</div>
                <ul class="flex flex-col w-full mb-4">
                    <li v-for="tag in manufacturer.tags['item-category'] ?? []">
                        <a
                            :href="
                                route('web.catalog.products.index', {
                                    filters: {
                                        0: {
                                            field: 'filter_category',
                                            values: [tag.name],
                                            type: 'any',
                                        },
                                        1: {
                                            field: 'filter_manufacturer',
                                            values: [manufacturer.name],
                                            type: 'any',
                                        },
                                    },
                                })
                            "
                            class="whitespace-nowrap truncate active:bg-gray-225 text-left w-full rounded-sm font-medium block py-2.5 px-3 hover:type-primary hover:bg-gray-tier-5 text-gray-600 bg-inherit dark:text-gray-450"
                        >
                            {{ tag.name }}
                        </a>
                    </li>
                </ul>
                <a
                    :href="
                        route('web.catalog.products.index', {
                            filters: {
                                1: {
                                    field: 'filter_manufacturer',
                                    values: [manufacturer.name],
                                    type: 'any',
                                },
                            },
                        })
                    "
                    class="w-fit flex items-center gap-1 border-b border-transparent text-gray-600 hover:border-gray-tier-1 dark:text-gray-450 dark:hover:border-gray-450"
                >
                    {{ $t("View all products") }}
                    <BaseSvg name="icon-arrow-right-alt" />
                </a>
            </nav>
        </div>
        </LoadingProgress>
    </div>
</template>

<script setup>
import {
    BaseSvg,
    ManufacturerDetails,
    SeclockMultiselect,
    ResourceList,
    Breadcrumbs,
    RecordsNotFound,
    LoadingProgress,
} from "@/Components";
import { computed, ref } from "vue";
import { useAssetTransforms, useUser } from "@/Composables";
import {router} from "@inertiajs/vue3";

const { settings } = useUser();

const { marketingAssetUrl } = useAssetTransforms();
const props = defineProps(["resources", "manufacturer", "crumbs", 'manufacturers']);
const selectedSeries = ref(null);
const selectedManufacturer = ref(props.manufacturers.find(company => company.display_name === props.manufacturer.name));

const navigateToShow = (event) => router.visit(route('web.catalog.resources.show', event.slug));

const clearSelect = () => (selectedSeries.value = null);

const filteredResources = computed(() => {
    let resources;
    if (selectedSeries.value?.id) {
        resources = Object.entries(props.manufacturer.resources)
            .map(([key, value]) => [
                key,
                value.filter((resource) =>
                    resource.series.includes(selectedSeries.value.id)
                )
            ])
            .filter(([_, filteredResources]) => filteredResources.length > 0)
            .reduce((obj, [key, filteredResources]) => {
                obj[key] = filteredResources;
                return obj;
            }, {});
    } else {
        resources = props.manufacturer?.resources;
    }

    // Sort the resources by key in alphabetical order
    return Object.keys(resources)
        .sort()
        .reduce((obj, key) => {
            obj[key] = resources?.[key];
            return obj;
        }, {});
});


const computed_series = computed(() => {
    //Add the empty entry "All Series" to the drop down selections
    return [{name: 'All Series'}, ...props.manufacturer?.series]
});
</script>

<style>
.single-manufacturer-select .multiselect {
    border: none;
}

.single-manufacturer-select .multiselect-search {
    align-items: center;
    display: flex;
    height: 100%;
}

.single-manufacturer-select .multiselect-search,
.single-manufacturer-select .multiselect-placeholder,
.single-manufacturer-select .multiselect-single-label {
    color: #25282a;
    font-size: 2.25rem;
    font-weight: 700;
    padding-left: 0;
}

.single-manufacturer-select .multiselect-caret {
    background-size: cover;
    height: 2rem;
    width: 2rem;
}

.dark .single-manufacturer-select .multiselect {
    background-color: transparent;
}

.dark .single-manufacturer-select .multiselect-search,
.dark .single-manufacturer-select .multiselect-placeholder,
.dark .single-manufacturer-select .multiselect-single-label {
    color: #fff;
}

.resource-select .multiselect {
    @apply rounded-none py-1 px-2 border-b-0 at1024:border-b at768:p-2 at1024:rounded-sm border-gray-150 at1024:border-gray-tier-7 border-x-0 at1024:border-x;
}
</style>
